import React, { useState, useRef } from 'react';
import { parseAnkiFile } from '../utils/ankiImport.js';

// Parse CSV/TSV: returns [{ question, answer }]
function parseCSV(text) {
    const cards = [];
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    for (const line of lines) {
        // Try tab first (Quizlet export), then comma
        const sep = line.includes('\t') ? '\t' : ',';
        const parts = line.split(sep);
        if (parts.length >= 2) {
            const question = parts[0].replace(/^["']|["']$/g, '').trim();
            const answer = parts.slice(1).join(sep).replace(/^["']|["']$/g, '').trim();
            if (question && answer) cards.push({ question, answer });
        }
    }
    return cards;
}

export default function ImportModal({ folders, onImport, onClose }) {
    const [tab, setTab] = useState('csv'); // 'csv' | 'anki'
    const [csvText, setCsvText] = useState('');
    const [preview, setPreview] = useState([]);
    const [targetFolderId, setTargetFolderId] = useState(folders[0]?.id || '');
    const [newFolderName, setNewFolderName] = useState('');
    const [useNewFolder, setUseNewFolder] = useState(false);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState('');
    const [ankiParsed, setAnkiParsed] = useState([]);
    const [ankiFileName, setAnkiFileName] = useState('');
    const fileInputRef = useRef(null);

    const handleCSVParse = () => {
        const cards = parseCSV(csvText);
        setPreview(cards);
        setError(cards.length === 0 ? 'No valid cards found. Use two columns: question, answer.' : '');
    };

    const handleAnkiFile = async (file) => {
        setError('');
        setAnkiParsed([]);
        setAnkiFileName(file.name);
        try {
            const cards = await parseAnkiFile(file);
            setAnkiParsed(cards);
            setPreview(cards);
            if (cards.length === 0) setError('No cards found in this .apkg file.');
        } catch (err) {
            setError(`Failed to read .apkg: ${err.message}`);
        }
    };

    const handleImport = async () => {
        const cards = tab === 'csv' ? parseCSV(csvText) : ankiParsed;
        if (cards.length === 0) { setError('No cards to import.'); return; }
        setImporting(true);
        try {
            const folderName = useNewFolder ? newFolderName.trim() : null;
            await onImport({ cards, folderId: useNewFolder ? null : targetFolderId, folderName });
            onClose();
        } catch (err) {
            setError(err.message || 'Import failed.');
        } finally {
            setImporting(false);
        }
    };

    const cardCount = preview.length;

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="w-full max-w-xl rounded-2xl border border-white/10 bg-slate-900/98 shadow-2xl flex flex-col overflow-hidden" style={{ maxHeight: '90vh' }}>
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/8 shrink-0">
                    <div>
                        <h2 className="text-base font-semibold text-white">Import Cards</h2>
                        <p className="text-xs text-slate-400 mt-0.5">From CSV, Quizlet export, or Anki .apkg</p>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition">
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                    {/* Tab switch */}
                    <div className="flex items-center gap-1 rounded-xl bg-white/5 p-1">
                        {[{ id: 'csv', label: 'CSV / Quizlet' }, { id: 'anki', label: 'Anki (.apkg)' }].map(t => (
                            <button key={t.id} onClick={() => { setTab(t.id); setPreview([]); setError(''); }}
                                className={`flex-1 rounded-lg py-2 text-sm font-medium transition ${tab === t.id ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {tab === 'csv' ? (
                        <div className="space-y-3">
                            <p className="text-xs text-slate-400">Paste two columns separated by a tab or comma. Works with Quizlet exports (Copy All → paste here).</p>
                            <textarea
                                value={csvText}
                                onChange={e => { setCsvText(e.target.value); setPreview([]); }}
                                rows={6}
                                placeholder={'Term\tDefinition\nPhotosynthesis\tConversion of light to glucose\nMitosis\tCell division...'}
                                className="w-full rounded-xl bg-slate-800/60 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/40 font-mono resize-y"
                            />
                            <button onClick={handleCSVParse} disabled={!csvText.trim()}
                                className="w-full rounded-xl py-2.5 text-sm font-medium text-white bg-white/8 hover:bg-white/12 border border-white/10 disabled:opacity-40 transition">
                                Preview ({parseCSV(csvText).length} cards found)
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <p className="text-xs text-slate-400">Select an Anki deck file (.apkg). Cards will be extracted from the first two fields of each note.</p>
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/15 bg-white/3 py-10 cursor-pointer hover:border-teal-500/40 hover:bg-teal-500/5 transition"
                            >
                                <svg className="h-10 w-10 text-slate-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <p className="text-sm text-slate-300 font-medium">{ankiFileName || 'Click to select .apkg file'}</p>
                                <p className="text-xs text-slate-500 mt-1">Supports Anki 2.0 and 2.1 format</p>
                            </div>
                            <input ref={fileInputRef} type="file" accept=".apkg" className="hidden"
                                onChange={e => e.target.files?.[0] && handleAnkiFile(e.target.files[0])} />
                        </div>
                    )}

                    {/* Preview */}
                    {preview.length > 0 && (
                        <div>
                            <p className="text-xs font-semibold text-slate-400 mb-2">{cardCount} cards found — preview:</p>
                            <div className="rounded-xl border border-white/8 bg-slate-950/40 overflow-hidden max-h-40 overflow-y-auto">
                                {preview.slice(0, 20).map((c, i) => (
                                    <div key={i} className="grid grid-cols-2 gap-2 px-3 py-2 border-b border-white/5 last:border-0 text-xs">
                                        <span className="text-slate-200 truncate">{c.question}</span>
                                        <span className="text-slate-400 truncate">{c.answer}</span>
                                    </div>
                                ))}
                                {preview.length > 20 && (
                                    <p className="text-center text-xs text-slate-500 py-2">+ {preview.length - 20} more</p>
                                )}
                            </div>
                        </div>
                    )}

                    {error && <p className="text-sm text-rose-300 bg-rose-500/10 rounded-xl px-3 py-2">{error}</p>}

                    {/* Destination */}
                    {(preview.length > 0) && (
                        <div className="space-y-3">
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Save to</p>
                            <div className="flex items-center gap-3">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" checked={!useNewFolder} onChange={() => setUseNewFolder(false)} className="accent-teal-500" />
                                    <span className="text-sm text-slate-300">Existing folder</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="radio" checked={useNewFolder} onChange={() => setUseNewFolder(true)} className="accent-teal-500" />
                                    <span className="text-sm text-slate-300">New folder</span>
                                </label>
                            </div>
                            {!useNewFolder ? (
                                <select value={targetFolderId} onChange={e => setTargetFolderId(e.target.value)}
                                    className="w-full rounded-xl bg-slate-800/70 border border-white/10 px-3 py-2.5 text-sm text-slate-200 focus:outline-none">
                                    {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                </select>
                            ) : (
                                <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                                    placeholder="New folder name"
                                    className="w-full rounded-xl bg-slate-800/70 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/40" />
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="shrink-0 flex items-center justify-end gap-3 px-5 py-4 border-t border-white/8">
                    <button onClick={onClose} className="rounded-xl px-4 py-2.5 text-sm font-medium text-slate-300 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition">
                        Cancel
                    </button>
                    <button
                        onClick={handleImport}
                        disabled={importing || preview.length === 0 || (useNewFolder && !newFolderName.trim()) || (!useNewFolder && !targetFolderId)}
                        className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-emerald-600 hover:opacity-90 disabled:opacity-40 transition"
                    >
                        {importing ? 'Importing…' : `Import ${cardCount} cards`}
                    </button>
                </div>
            </div>
        </div>
    );
}
